import { GenericError } from '../../errors/shared/genericError.js';
import { Ok, Err } from '../../result/shared/result.js';
import { kStructuredCloneMarker, kSendMarker, isStructuredClone, Clone } from '../../threads/shared/transfer/transfer.js';
import { restore } from '../../threads/shared/transfer/restoreRegistry.js';
import '../../iterator/shared/iterator.js';

/* oxlint-disable max-lines */
const NAMESPACE = 'core.option';
const SomeType = 'Some';
const NoneType = 'None';
class SomeImpl {
    '@type' = SomeType;
    [kStructuredCloneMarker] = NAMESPACE;
    [kSendMarker] = 1;
    Some;
    constructor(val) {
        this.Some = val;
    }
    isNone() {
        return false;
    }
    isSomeAnd(f) {
        return f(this.Some);
    }
    isSome() {
        return true;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    expect(msg, domain) {
        return this.Some;
    }
    unwrap() {
        return this.Some;
    }
    unwrapOr() {
        return this.Some;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    unwrapOrElse(f) {
        return this.Some;
    }
    map(f) {
        return Some(f(this.Some));
    }
    mapOr(defaultValue, f) {
        return f(this.Some);
    }
    mapOrElse(defaultValue, f) {
        return f(this.Some);
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    okOr(err) {
        return Ok(this.Some);
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    okOrElse(err) {
        return Ok(this.Some);
    }
    andThen(f) {
        return f(this.Some);
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    or(optb) {
        return this;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    orElse(f) {
        return this;
    }
    xor(optb) {
        return optb.isNone() ? this : None();
    }
    toStructuredClone() {
        if (isStructuredClone(this.Some)) {
            const [marker, val, transferable] = this.Some.toStructuredClone();
            let someStruct;
            if (transferable) {
                someStruct = [this[kStructuredCloneMarker], { Some: [marker, val] }, transferable];
            }
            else {
                someStruct = [this[kStructuredCloneMarker], { Some: [marker, val] }];
            }
            return someStruct;
        }
        const someStruct = [this[kStructuredCloneMarker], { Some: this.Some }];
        return someStruct;
    }
    *[Symbol.iterator]() {
        yield this;
    }
}
class NoneImpl {
    '@type' = NoneType;
    [kStructuredCloneMarker] = NAMESPACE;
    [kSendMarker] = 1;
    None;
    isSome() {
        return false;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    isSomeAnd(f) {
        return false;
    }
    isNone() {
        return true;
    }
    expect(message, domain) {
        throw new GenericError({
            type: 'OptionExpectPanic',
            message,
            domain: domain || NAMESPACE + '.err.expect',
        });
    }
    unwrap(domain) {
        throw new GenericError({
            type: 'OptionUnwrapPanic',
            message: 'called `Option.unwrap()` on a `None` value',
            domain: domain || NAMESPACE + '.err.unwrap',
        });
    }
    unwrapOr(defaultValue) {
        return defaultValue;
    }
    unwrapOrElse(f) {
        return f();
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    map(f) {
        return None();
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    mapOr(defaultValue, f) {
        return defaultValue;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    mapOrElse(defaultValue, f) {
        return defaultValue();
    }
    okOr(err) {
        return Err(err);
    }
    okOrElse(err) {
        return Err(err());
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    andThen(f) {
        return None();
    }
    or(optb) {
        return optb;
    }
    orElse(f) {
        return f();
    }
    xor(optb) {
        return optb.isSome() ? optb : this;
    }
    toStructuredClone() {
        // oxlint-disable-next-line typescript/no-explicit-any
        return [this[kStructuredCloneMarker], { None: 0 }];
    }
    *[Symbol.iterator]() {
        yield this;
    }
}
function Some(val) {
    return new SomeImpl(val);
}
function None() {
    return new NoneImpl();
}
const Option = {
    *iterate(iterable) {
        for (const result of iterable) {
            if (result.isSome()) {
                yield result.Some;
            }
        }
    },
    collect(iterable) {
        return Array.from(Option.iterate(iterable));
    },
    is(val) {
        return Option.isStruct(val) && '@type' in val && (val['@type'] === SomeType || val['@type'] === NoneType);
    },
    isStruct(val) {
        if (typeof val !== 'object' || val === null) {
            return false;
        }
        return (Object.prototype.hasOwnProperty.call(val, 'Some') ||
            Object.prototype.hasOwnProperty.call(val, 'None'));
    },
    isSomeStruct(val) {
        return Option.isStruct(val) && Object.prototype.hasOwnProperty.call(val, 'Some');
    },
    isNoneStruct(val) {
        return Option.isStruct(val) && Object.prototype.hasOwnProperty.call(val, 'None');
    },
    fromStruct(val) {
        if (Option.isSomeStruct(val)) {
            if (Clone.isStruct(val.Some)) {
                const result = restore(val.Some).map(val => Some(val));
                return result;
            }
            return Ok(Some(val.Some));
        }
        return Ok(None());
    },
    restore() {
        return {
            [kStructuredCloneMarker]: NAMESPACE,
            from(serialized) {
                return Option.fromStruct(serialized).unwrap();
            },
        };
    },
    /**
     * Трансформирует значение в `Option` через конвертацию в boolean
     *
     * ```js
     * assert.deepEqual(Option.toPositive(undefined), None());
     * assert.deepEqual(Option.toPositive(''), None());
     * assert.deepEqual(Option.toPositive(false), None());
     *
     * assert.deepEqual(Option.toPositive({}), Some({}));
     * assert.deepEqual(Option.toPositive('s'), Some('s'));
     * ```
     *
     * @template T
     * @param {(T | unknown)} val
     * @return {Option<T>}
     */
    toPositive(val) {
        if (!val) {
            return None();
        }
        return Some(val);
    },
    undefinedToOption(val) {
        return val === undefined ? None() : Some(val);
    },
};

export { None, Option, Some };
//# sourceMappingURL=option.js.map
